import {useState, useRef, useEffect, useMemo} from 'react'
import {ChevronLeft, ChevronRight, X, CalendarDays} from 'lucide-react'
import type {Board, Task} from '../../types'
import {useApp} from '../../context/AppContext'
import {getDaysInMonth, getFirstDayOfMonth, getPriorityConfig} from '../../utils/helpers'
import type {BoardFilters} from './BoardFilters'

interface CalendarViewProps {
  board: Board
  filters?: BoardFilters
}

const MONTH_NAMES = [
  'Janeiro',
  'Fevereiro',
  'Março',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro',
]

const DAY_NAMES = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sab']

interface DayTasksModalProps {
  day: number
  month: number
  year: number
  tasks: Task[]
  onClose: () => void
  onSelectTask: (taskId: string) => void
}

function DayTasksModal({day, month, year, tasks, onClose, onSelectTask}: DayTasksModalProps) {
  const modalRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose()
    }

    function handleClickOutside(e: MouseEvent) {
      if (modalRef.current && !modalRef.current.contains(e.target as Node)) {
        onClose()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    document.addEventListener('mousedown', handleClickOutside)
    document.body.style.overflow = 'hidden'

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      document.removeEventListener('mousedown', handleClickOutside)
      document.body.style.overflow = ''
    }
  }, [onClose])

  const dateLabel = `${String(day).padStart(2, '0')} de ${MONTH_NAMES[month]} de ${year}`

  return (
    <div className='fixed inset-0 z-[999] flex items-center justify-center bg-slate-900/50 backdrop-blur-[2px] p-4'>
      <div
        ref={modalRef}
        className='w-full max-w-2xl max-h-[85vh] bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden animate-fade-in'
      >
        <div className='flex items-center justify-between gap-3 px-4 md:px-6 py-4 border-b border-slate-100 bg-slate-50'>
          <div className='min-w-0'>
            <div className='flex items-center gap-2 text-slate-500 mb-1'>
              <CalendarDays size={16} />
              <span className='text-xs md:text-sm font-medium'>Tarefas do dia</span>
            </div>
            <h3 className='text-sm md:text-lg font-semibold text-slate-800 truncate'>
              {dateLabel}
            </h3>
            <p className='text-xs md:text-sm text-slate-500 mt-1'>
              {tasks.length} {tasks.length === 1 ? 'tarefa' : 'tarefas'}
            </p>
          </div>

          <button
            onClick={onClose}
            className='shrink-0 p-2 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-200 transition-colors'
            aria-label='Fechar modal'
          >
            <X size={18} />
          </button>
        </div>

        <div className='p-3 md:p-4 overflow-y-auto max-h-[calc(85vh-96px)]'>
          {tasks.length === 0 ? (
            <div className='py-10 text-center text-sm text-slate-500'>
              Nenhuma tarefa para este dia.
            </div>
          ) : (
            <div className='space-y-3'>
              {tasks.map((task) => {
                const p = getPriorityConfig(task.priority)

                return (
                  <button
                    key={task.id}
                    onClick={() => {
                      onSelectTask(task.id)
                      onClose()
                    }}
                    className='w-full text-left rounded-xl border border-slate-200 bg-white hover:border-brand-300 hover:shadow-sm transition-all p-4'
                  >
                    <div className='flex items-start gap-3'>
                      <span
                        className={`mt-1 inline-block w-3 h-3 rounded-full shrink-0 ${p.dot}`}
                      />

                      <div className='min-w-0 flex-1'>
                        <div className='flex flex-col md:flex-row md:items-start md:justify-between gap-2'>
                          <h4 className='text-sm md:text-base font-semibold text-slate-800 break-words leading-5'>
                            {task.title}
                          </h4>

                          <span className='shrink-0 inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-semibold bg-slate-100 text-slate-600 capitalize'>
                            {task.priority}
                          </span>
                        </div>

                        {task.description && (
                          <div className='mt-2 max-h-32 overflow-y-auto rounded-lg bg-slate-50 px-3 py-2'>
                            <div
                              className='text-sm text-slate-600 break-words prose prose-sm max-w-none prose-p:my-2 prose-ul:my-2 prose-ol:my-2 prose-li:my-0'
                              dangerouslySetInnerHTML={{__html: task.description}}
                            />
                          </div>
                        )}

                        <div className='mt-3 flex flex-wrap items-center gap-2'>
                          <span className='inline-flex items-center rounded-lg px-2.5 py-1 text-[11px] font-medium bg-slate-100 text-slate-600'>
                            ID: {task.id.slice(0, 8)}
                          </span>

                          {task.status && (
                            <span className='inline-flex items-center rounded-lg px-2.5 py-1 text-[11px] font-medium bg-brand-50 text-brand-700'>
                              {task.status}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </button>
                )
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default function CalendarView({board, filters}: CalendarViewProps) {
  const {getTasksForBoard, dispatch} = useApp()
  const today = new Date()

  const [year, setYear] = useState(today.getFullYear())
  const [month, setMonth] = useState(today.getMonth())
  const [modalDay, setModalDay] = useState<number | null>(null)

  const allTasks = getTasksForBoard(board.id).filter((t) => t.dueDate)

  const tasks = useMemo(() => {
    if (!filters) return allTasks

    return allTasks.filter((task) => {
      if (filters.search) {
        const searchLower = filters.search.toLowerCase()
        if (
          !task.title.toLowerCase().includes(searchLower) &&
          !task.description.toLowerCase().includes(searchLower)
        ) {
          return false
        }
      }

      if (filters.status.length > 0 && !filters.status.includes(task.status)) {
        return false
      }

      if (filters.priority.length > 0 && !filters.priority.includes(task.priority)) {
        return false
      }

      if (filters.assigneeIds.length > 0) {
        const hasAssignee = filters.assigneeIds.some((id) => task.assigneeIds.includes(id))
        if (!hasAssignee) return false
      }

      if (filters.labelIds.length > 0) {
        const hasLabel = filters.labelIds.some((id) => task.labelIds.includes(id))
        if (!hasLabel) return false
      }

      return true
    })
  }, [allTasks, filters])

  const daysInMonth = getDaysInMonth(year, month)
  const firstDay = getFirstDayOfMonth(year, month)

  const prevMonth = () => {
    if (month === 0) {
      setMonth(11)
      setYear(year - 1)
    } else {
      setMonth(month - 1)
    }
    setModalDay(null)
  }

  const nextMonth = () => {
    if (month === 11) {
      setMonth(0)
      setYear(year + 1)
    } else {
      setMonth(month + 1)
    }
    setModalDay(null)
  }

  const getTasksForDay = (day: number) => {
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`
    return tasks.filter((t) => t.dueDate && t.dueDate.startsWith(dateStr))
  }

  const isToday = (day: number) => {
    return today.getFullYear() === year && today.getMonth() === month && today.getDate() === day
  }

  const blanks = Array.from({length: firstDay}, (_, i) => i)
  const days = Array.from({length: daysInMonth}, (_, i) => i + 1)

  const MAX_VISIBLE = 3
  const selectedDayTasks = modalDay ? getTasksForDay(modalDay) : []

  return (
    <div>
      <div className='devops-surface overflow-hidden'>
        <div className='flex items-center justify-between px-6 py-4 border-b border-slate-100'>
          <h3 className='text-lg font-semibold text-slate-800'>
            {MONTH_NAMES[month]} {year}
          </h3>

          <div className='flex items-center gap-1'>
            <button
              onClick={prevMonth}
              className='p-2 rounded-lg text-slate-500 hover:text-slate-700 hover:bg-slate-100 transition-colors'
              aria-label='Mes anterior'
            >
              <ChevronLeft size={18} />
            </button>

            <button
              onClick={() => {
                setYear(today.getFullYear())
                setMonth(today.getMonth())
                setModalDay(null)
              }}
              className='px-3 py-1.5 rounded-lg text-xs font-medium text-brand-600 hover:bg-brand-50 transition-colors'
            >
              Hoje
            </button>

            <button
              onClick={nextMonth}
              className='p-2 rounded-lg text-slate-500 hover:text-slate-700 hover:bg-slate-100 transition-colors'
              aria-label='Proximo mes'
            >
              <ChevronRight size={18} />
            </button>
          </div>
        </div>

        <div className='overflow-x-auto'>
          <div className='grid min-w-[760px] grid-cols-7'>
            {DAY_NAMES.map((day) => (
              <div
                key={day}
                className='py-2 text-center text-xs font-semibold uppercase tracking-wider text-slate-400 border-b border-slate-100'
              >
                {day}
              </div>
            ))}

            {blanks.map((i) => (
              <div
                key={`blank-${i}`}
                className='min-h-[90px] md:min-h-[120px] border-b border-r border-slate-100 bg-slate-50/50'
              />
            ))}

            {days.map((day) => {
              const dayTasks = getTasksForDay(day)
              const todayClass = isToday(day)
              const extraCount = dayTasks.length - MAX_VISIBLE

              return (
                <div
                  key={day}
                  className={`min-h-[90px] md:min-h-[120px] border-b border-r border-slate-100 p-2 ${
                    todayClass ? 'bg-brand-50/40' : 'hover:bg-slate-50'
                  } transition-colors`}
                >
                  <span
                    className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-semibold ${
                      todayClass ? 'bg-brand-500 text-white' : 'text-slate-600'
                    }`}
                  >
                    {day}
                  </span>

                  <div className='mt-2 space-y-1'>
                    {dayTasks.slice(0, MAX_VISIBLE).map((task) => {
                      const p = getPriorityConfig(task.priority)

                      return (
                        <button
                          key={task.id}
                          onClick={() => dispatch({type: 'SELECT_TASK', payload: task.id})}
                          className='w-full text-left px-2 py-1 rounded-md text-[11px] md:text-xs font-medium hover:bg-slate-100 transition-colors'
                          title={task.title}
                        >
                          <div className='flex items-start gap-1.5 min-w-0'>
                            <span
                              className={`inline-block w-2 h-2 rounded-full mt-1 shrink-0 ${p.dot}`}
                            />
                            <span className='text-slate-700 truncate'>{task.title}</span>
                          </div>
                        </button>
                      )
                    })}

                    {extraCount > 0 && (
                      <button
                        type='button'
                        onClick={(e) => {
                          e.stopPropagation()
                          setModalDay(day)
                        }}
                        className='text-[11px] md:text-xs font-semibold text-brand-600 hover:text-brand-700 hover:bg-brand-50 rounded-md px-2 py-1 transition-colors w-full text-left'
                      >
                        +{extraCount} {extraCount === 1 ? 'tarefa' : 'tarefas'}
                      </button>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </div>

      {modalDay !== null && (
        <DayTasksModal
          day={modalDay}
          month={month}
          year={year}
          tasks={selectedDayTasks}
          onClose={() => setModalDay(null)}
          onSelectTask={(taskId) => dispatch({type: 'SELECT_TASK', payload: taskId})}
        />
      )}
    </div>
  )
}
